import { BarChartResultResponse } from '@business/entities/dashboard';
import { Formatter } from '@core/utils/formatter';
import 'chartist/dist/scss/chartist.scss';
import React, { memo, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Card, CardTitle } from 'reactstrap';
import { WidgetProps } from '../types';
import { WidgetIcons } from '../BaseWidget';
import { BarChartWidgetConfigData } from './BarChartWidgetConfig';
import { R } from '@core/utils/r';
import { durationHumanizeReports } from '@core/utils/time';

export const BarChartWidget = memo((props: WidgetProps<BarChartResultResponse, BarChartWidgetConfigData>) => {
  const { settings, result, config, isPreview = false } = props;
  const colors = useMemo(() => {
    const color = R.get(config, 'color');
    if (color) return [color];
    return ['#34c38f'];
  }, [config]);
  const horizontal = useMemo(() => R.get(config, 'layout') == 'horizonal', [config]);

  const labels = useMemo(() => {
    if (!result && isPreview)
      return ['South Korea', 'Canada', 'United Kingdom', 'Netherlands', 'Italy', 'France', 'Japan', 'United States', 'China', 'Germany'];
    if (!result) return [];

    if (!R.isEmpty(R.get(config, 'naValue'))) {
      return result.labels.map(label => {
        if (label == 'N/A') {
          return R.get(config, 'naValue');
        } else {
          if (R.isString(label)) {
            try {
              const result = JSON.parse(label);
              if (R.isArray(result)) {
                return result.join(', ');
              }
              return label;
            } catch (e) {
              return label;
            }
          }
        }
      });
    }
    return result.labels;
  }, [config, isPreview, result]);

  const series = [
    {
      name: '',
      data: result ? result.series : isPreview ? [380, 430, 450, 475, 550, 584, 780, 1100, 1220, 1365] : [],
    },
  ];
  const options = {
    chart: {
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: R.get(config, 'showValuesOnGraph'),
      style: {
        colors: ['#000'],
      },
      offsetY: horizontal ? 0 : -20,
      offsetX: horizontal ? 20 : 0,
    },
    colors,
    grid: {
      borderColor: '#f1f1f1',
    },
    xaxis: {
      categories: labels,
    },
    tooltip: {
      y: {
        formatter: (value: number) => {
          if (result?.label == '%') {
            return Formatter.formatPercent(value, 2);
          } else if (result?.label == 'd') {
            return durationHumanizeReports(value * 60, { round: true });
          } else if (result?.label == 'h') {
            return durationHumanizeReports(value * 60, { units: ['h'] });
          } else if (result?.label == 'm') {
            return durationHumanizeReports(value * 60, { units: ['m'] });
          }
          return result?.label ? Formatter.formatCurrency(value) : Formatter.formatInteger(value);
        },
      },
    },
  };

  return (
    <Card className="p-4" style={{ marginBottom: 0, flex: 1 }}>
      <CardTitle className="mb-4 d-flex align-items-center flex-row">
        {settings.title || 'Bar Chart'} <WidgetIcons {...props} />
      </CardTitle>
      <div id="bar-chart" className="e-chart">
        <ReactApexChart options={options} series={series} type="bar" height={settings.grid.h * 100 - 50} />
      </div>
    </Card>
  );
});
